import { write, getColumnsCount } from './write.js';
import { moveDownLines, moveUpLines, clearLine, clearScreen, stripAnsi, visibleLineCount } from './ansiHelpers.js';
import { Sticky } from './Sticky.js';
import fs from 'fs';
import path from 'path';

/** Update interval in milliseconds. This is throttled for better performance. */
export const stickyUpdateIntervalMs = 250;

export class Writer {
  private _stickies: Sticky[] = [];
  private _lastStickyLineCount: number = 0;
  private _totalLines: number = 0;
  private _reprintTimer: NodeJS.Timeout | undefined;
  private _logFileStream: fs.WriteStream | undefined;

  /**
   * Set stickies. If there are no stickies, it will clear immediately.
   * Otherwise it will wait until the next throttled reprint to update.
   */
  setStickies(newStickies: (Sticky | string)[]): void {
    this._stickies = newStickies.map((s) => (typeof s === 'string' ? new Sticky(s) : s));

    if (this._stickies.length) {
      if (!this._reprintTimer) {
        // If there was no scheduled write, trigger one now. Otherwise just wait for the schedule.
        this._reprintStickiesNow();
      }
    } else {
      this.cancelPendingReprint();
      this._clearPreviousStickies();
    }
  }

  /**
   * Write the text to the terminal. If there are stickies, will clear them first then re-print.
   * Also writes to a log file if enabled.
   */
  write(text: string): void {
    this._clearPreviousStickies();
    write(`${text}\n`);
    this._totalLines += visibleLineCount(text, getColumnsCount());
    this._printStickies();

    // Only process and write to log file when it's enabled, to avoid the stripAnsi overhead
    if (this._logFileStream) {
      try {
        this._logFileStream.write(`${stripAnsi(text)}\n`);
      } catch {
        // Do not console error here, as it will cause recursive calls to the writer.
        // This is a silent failure to avoid breaking the main functionality.
      }
    }
  }

  /** Print the stickies and schedule a throttled update */
  private _printStickies(): void {
    this._lastStickyLineCount = 0;

    if (this._stickies.length > 0) {
      const columns = getColumnsCount();
      for (const sticky of this._stickies) {
        // For spinning stickies, the text updates on each access, so save it upfront
        const stickyText = sticky.getText();
        write(`${stickyText}\n`);
        const lineCount = visibleLineCount(stickyText, columns);
        this._totalLines += lineCount;
        this._lastStickyLineCount += lineCount;
      }

      // Only schedule an update if there are any stickies
      this._reprintStickiesThrottled();
    }
  }

  /**
   * Clear any previous stickies from the terminal.
   * Note that to cancel future updates, you must call `cancelPendingReprint()` separately.
   */
  private _clearPreviousStickies(): void {
    if (this._lastStickyLineCount > 0 && this._totalLines > 0) {
      this._clearLines(this._lastStickyLineCount);
      this._lastStickyLineCount = 0;
    }
  }

  private _clearLines(count: number): void {
    // Make sure that the count is not negative and does not exceed the total number of lines.
    count = Math.min(Math.max(0, count), this._totalLines);

    // Clear the screen and move the cursor to the top left corner.
    moveUpLines(count);

    for (let i = 0; i < count; i++) {
      clearLine();
      moveDownLines(1);
    }

    moveUpLines(count);
    this._totalLines -= count;
  }

  /**
   * Clear all output and remove all stickies.
   */
  public clear(): void {
    this.cancelPendingReprint();
    this._stickies = [];
    this._lastStickyLineCount = 0;
    this._totalLines = 0;
    clearScreen();
  }

  /** Schedule a sticky reprint if not already scheduled */
  private _reprintStickiesThrottled(): void {
    if (!this._reprintTimer) {
      this._reprintTimer = setTimeout(() => {
        this._reprintTimer = undefined;
        this._reprintStickiesNow();
      }, stickyUpdateIntervalMs);
    }
  }

  /**
   * Reprint stickies immediately, bypassing any throttling (and reschedule the next throttled update)
   */
  private _reprintStickiesNow(): void {
    this._clearPreviousStickies();
    this._printStickies();
  }

  /** Cancel any pending throttled repaint. */
  cancelPendingReprint(): void {
    if (this._reprintTimer) {
      clearTimeout(this._reprintTimer);
      this._reprintTimer = undefined;
    }
  }

  /** Remove a sticky and trigger a reprint */
  removeSticky(sticky: Sticky): void {
    this.setStickies(this._stickies.filter((s) => s !== sticky));
  }

  enableLogFile(cwd?: string): void {
    if (this._logFileStream) {
      try {
        this._logFileStream.end();
      } catch (err) {
        console.error(`Failed to close existing log file stream: ${err}`);
      }
    }

    const timestamp = new Date().toISOString().replace(/:/g, '-');
    // eslint-disable-next-line no-restricted-properties
    const dirname = cwd ?? process.cwd();
    const filePath = path.join(dirname, `cloudpack-${timestamp}.log`);

    try {
      fs.mkdirSync(dirname, { recursive: true });
      this._logFileStream = fs.createWriteStream(filePath, { flags: 'a' });
    } catch (err) {
      console.error(`Failed to open log file: ${err}`);
    }
  }

  public dispose(): Promise<void> {
    this.cancelPendingReprint();
    return new Promise<void>((resolve) => {
      if (this._logFileStream) {
        // Write any remaining data and then close the stream
        this._logFileStream.end(() => {
          this._logFileStream = undefined;
          resolve();
        });
      } else {
        resolve();
      }
    });
  }
}
